// @ts-strict-ignore
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { CustomerEarningsSummaryResponse, Portfolio, RewardsProduct } from 'types/rewards'
import { RewardsSheet } from '../components/Sheet/RewardsSheet'
import { RewardsEmbeddedTabsType } from '../hooks/useRewardsEmbeddedTabs'
import { RewardsAction } from '../types'

interface RewardsSheetProviderProps {
  userPortfolios: Portfolio[]
  rewardsProducts: RewardsProduct[]
  selectedCurrency?: string
  isCustomerRewards?: boolean
  activeTab?: RewardsEmbeddedTabsType
  customerEarningsSummary?: CustomerEarningsSummaryResponse
  children: ReactNode
}

interface RewardsSheetContextValue {
  isSheetOpen: boolean
  rewardsSheetAction: RewardsAction | null
  userPortfolios: Portfolio[]
  rewardsProducts: RewardsProduct[]
  selectedCurrency: string | null
  isCustomerRewards: boolean
  activeTab?: RewardsEmbeddedTabsType
  customerEarningsSummary?: CustomerEarningsSummaryResponse
  handleSheetOpen: (action: Omit<RewardsAction, 'onClose'>) => void
  handleSheetUpdate: (action: Omit<RewardsAction, 'onClose' | 'actionId'>) => void
}
const RewardsSheetContext = createContext<RewardsSheetContextValue | null>(null)

export const useRewardsSheetContext = () => {
  const context = useContext(RewardsSheetContext)

  if (!context) {
    throw new Error('useRewardsActionContext must be used within a RewardsActionProvider')
  }
  return context
}

export const RewardsSheetProvider = ({
  userPortfolios,
  rewardsProducts,
  selectedCurrency,
  children,
  isCustomerRewards,
  activeTab,
  customerEarningsSummary,
}: RewardsSheetProviderProps) => {
  const [rewardsSheetAction, setRewardsSheetAction] = useState<RewardsAction | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const handleSheetOpen = (action: Omit<RewardsAction, 'onClose'>) => {
    setRewardsSheetAction({ ...action, onClose: () => setIsSheetOpen(false) })
    setIsSheetOpen(true)
  }

  const handleSheetUpdate = useCallback((action: Omit<RewardsAction, 'onClose' | 'actionId'>) => {
    setRewardsSheetAction(prev => ({
      ...action,
      actionId: prev?.actionId,
      onClose: () => setIsSheetOpen(false),
    }))
  }, [])

  const getSelectedCurrency = useCallback(() => {
    if (selectedCurrency) return selectedCurrency
    if (rewardsProducts.length === 1) return rewardsProducts[0]?.currency
    return null
  }, [selectedCurrency, rewardsProducts])

  const contextValue = useMemo<RewardsSheetContextValue>(
    () => ({
      isSheetOpen,
      rewardsSheetAction,
      userPortfolios,
      rewardsProducts,
      handleSheetOpen,
      handleSheetUpdate,
      selectedCurrency: getSelectedCurrency(),
      customerEarningsSummary,
      isCustomerRewards,
      activeTab,
    }),
    [
      isSheetOpen,
      rewardsSheetAction,
      handleSheetOpen,
      handleSheetUpdate,
      getSelectedCurrency,
      userPortfolios,
      rewardsProducts,
      customerEarningsSummary,
      isCustomerRewards,
      activeTab,
    ]
  )

  return (
    <RewardsSheetContext.Provider value={contextValue}>
      {children}
      <RewardsSheet isSheetOpen={isSheetOpen} />
    </RewardsSheetContext.Provider>
  )
}
